import React, { useState, ReactNode, CSSProperties, useEffect } from 'react'
import ReactDOM from 'react-dom'
import {
  StyledModalOverlay,
  StyledModal,
  ModalTitle,
  StyledModalHeader,
  StyledModalBody,
  StyledModalFooter,
  CloseButton,
} from './styles/Modal.styles'
import {
  ModalButtonConfirm,
  ModalButtonCancel,
  ModalButtonStandardGhost,
} from './styles/Button.styles'

// Type definitions
interface HeaderButton {
  onClick: () => void
  icon: ReactNode
  ariaLabel?: string
  style?: CSSProperties
}

interface ModalDefaultButtonFooterProps {
  confirmText?: string
  cancelText?: string
  onConfirm?: () => void
  onCancel?: () => void
  confirmColor?: string
  confirmGhost?: boolean
  flip?: boolean
}

interface ModalProps {
  children: ReactNode
  title?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | 'full'
  open?: boolean
  onClose: () => void
  confirmText?: string
  cancelText?: string
  onConfirm?: () => void
  onCancel?: () => void
  confirmColor?: string
  confirmGhost?: boolean
  flip?: boolean
  headerButtons?: HeaderButton[]
  [key: string]: any // For ...props spread
}

interface ModalHeaderProps {
  children: ReactNode
  onClose?: () => void
  headerButtons?: HeaderButton[]
}

interface ModalTitleProps {
  children: ReactNode
  [key: string]: any // For ...props spread
}

export const ModalDefaultButtonFooter: React.FC<
  ModalDefaultButtonFooterProps
> = ({
  confirmText = 'Confirm',
  cancelText,
  onConfirm,
  onCancel,
  confirmColor,
  confirmGhost = false,
  flip = false,
}) => {
  const buttons = [
    cancelText && (
      <ModalButtonCancel key="cancel" onClick={onCancel}>
        {cancelText}
      </ModalButtonCancel>
    ),
    confirmGhost ? (
      <ModalButtonStandardGhost key="confirm" onClick={onConfirm}>
        {confirmText}
      </ModalButtonStandardGhost>
    ) : (
      <ModalButtonConfirm
        key="confirm"
        onClick={onConfirm}
        style={confirmColor ? { backgroundColor: confirmColor } : undefined}
      >
        {confirmText}
      </ModalButtonConfirm>
    ),
  ].filter(Boolean)

  return <>{flip ? buttons.reverse() : buttons}</>
}

const Modal: React.FC<ModalProps> & {
  Header: React.FC<ModalHeaderProps>
  Title: React.FC<ModalTitleProps>
  Body: typeof StyledModalBody
  Footer: typeof StyledModalFooter
  DefaultFooter: typeof ModalDefaultButtonFooter
} = ({
  children,
  title,
  size,
  open = false,
  onClose,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  confirmColor,
  confirmGhost = false,
  flip = false,
  headerButtons = [],
  ...props
}) => {
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState<boolean>(false)

  useEffect(() => {
    // When the modal is opened, we want to prevent the background from scrolling
    if (open) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [open])

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only set to true if the click started on the overlay itself
    if (e.target === e.currentTarget) {
      setMouseDownOnOverlay(true)
    } else {
      setMouseDownOnOverlay(false)
    }
  }

  const handleOverlayMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if both mousedown and mouseup occurred on overlay
    if (mouseDownOnOverlay && e.target === e.currentTarget) {
      onClose()
    }

    setMouseDownOnOverlay(false)
  }

  if (!open) return null

  const modalContent = (
    <>
      {title && (
        <StyledModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {headerButtons.map((button, index) => (
              <CloseButton
                key={index}
                onClick={button.onClick}
                aria-label={button.ariaLabel || 'Header action'}
                style={button.style}
              >
                {button.icon}
              </CloseButton>
            ))}
            <CloseButton onClick={onClose} aria-label="Close">
              <span>×</span>
            </CloseButton>
          </div>
        </StyledModalHeader>
      )}
      {children}
      {(confirmText || cancelText) && (
        <StyledModalFooter>
          <ModalDefaultButtonFooter
            confirmText={confirmText}
            cancelText={cancelText}
            onConfirm={onConfirm}
            onCancel={onCancel}
            confirmColor={confirmColor}
            confirmGhost={confirmGhost}
            flip={flip}
          />
        </StyledModalFooter>
      )}
    </>
  )

  const modalElement = (
    <StyledModalOverlay
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
    >
      <StyledModal
        size={size}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        {...props}
      >
        {modalContent}
      </StyledModal>
    </StyledModalOverlay>
  )

  // Render modal using React Portal to ensure it's positioned relative to the viewport
  return ReactDOM.createPortal(modalElement, document.body)
}

Modal.Header = ({
  children,
  onClose,
  headerButtons = [],
}: ModalHeaderProps) => (
  <StyledModalHeader>
    {children}
    {onClose && (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {headerButtons.map((button, index) => (
          <CloseButton
            key={index}
            onClick={button.onClick}
            aria-label={button.ariaLabel || 'Header action'}
            style={button.style}
          >
            {button.icon}
          </CloseButton>
        ))}
        <CloseButton onClick={onClose} aria-label="Close">
          <span>×</span>
        </CloseButton>
      </div>
    )}
  </StyledModalHeader>
)

Modal.Title = ({ children, ...props }: ModalTitleProps) => (
  <ModalTitle {...props}>{children}</ModalTitle>
)

Modal.Body = StyledModalBody
Modal.Footer = StyledModalFooter
Modal.DefaultFooter = ModalDefaultButtonFooter

export default Modal
